import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, MOIS_LABELS } from '../../utils/calculs'
import { getAnneeScolaireEnCours, labelAnneeScolaire, plageAnneeScolaire, listeAnneesScolaires } from '../../utils/anneeScolaire'

// Une vue par facture : SPA, Hydro, Accueil, Assistanat, Réunions, ou Global (somme de tout)
const VUES = [
  { v: 'SPA', label: 'SPA' },
  { v: 'HYDRO', label: 'Hydrothérapie' },
  { v: 'ACCUEIL', label: 'Accueil' },
  { v: 'ASSISTANAT', label: 'Assistanat' },
  { v: 'REUNIONS', label: 'Réunions' },
  { v: 'GLOBAL', label: 'Global' },
]
const TYPES = ['SPA', 'HYDRO', 'ACCUEIL', 'ASSISTANAT', 'REUNIONS']
const LABEL_TYPE = { SPA: 'SPA', HYDRO: 'Hydro', ACCUEIL: 'Accueil', ASSISTANAT: 'Assistanat', REUNIONS: 'Réunions' }
const UNITE = { SPA: 'Jours', HYDRO: 'Jours', ACCUEIL: 'Jours', ASSISTANAT: 'Jours', REUNIONS: 'Présences' }

const vide = () => ({ montant: 0, reelle: 0, ca_ttc: 0, jours: 0 })
function ajouter(a, l) {
  a.montant += l.montant
  a.reelle += l.reelle || 0
  a.ca_ttc += l.ca_ttc || 0
  a.jours += l.jour
}

export default function RecapAnnuel() {
  const [annee, setAnnee] = useState(getAnneeScolaireEnCours())
  const [vue, setVue] = useState('SPA')
  const [seances, setSeances] = useState([])
  const [superviseurs, setSuperviseurs] = useState([])
  const [financier, setFinancier] = useState([])
  const [accueil, setAccueil] = useState([])
  const [reunions, setReunions] = useState([])
  const [assistanat, setAssistanat] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => { load() }, [annee]) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    const { debut, fin } = plageAnneeScolaire(annee)

    const [{ data: sea }, { data: sups }, { data: saisies }, { data: acc }, { data: reu }, { data: assist }] = await Promise.all([
      supabase.from('seances')
        .select('date, pole, ca_ht, ca_ttc, remuneration_reelle, remuneration_affichee, total_massage_payant, total_massage_gratuit, superviseur_id, superviseur2_id')
        .gte('date', debut).lte('date', fin),
      supabase.from('profiles')
        .select('id, prenom, nom').eq('role', 'superviseur').order('prenom'),
      supabase.from('saisies_financieres')
        .select('date_debut, cb_pin, cb_sans_contact, cheque_comptant, cheque_differe, especes')
        .gte('date_debut', debut).lte('date_debut', fin),
      supabase.from('seances_accueil')
        .select('date, montant, superviseur_id').gte('date', debut).lte('date', fin),
      supabase.from('reunions_presences')
        .select('date, montant, superviseur_id').gte('date', debut).lte('date', fin),
      // Assistanat : seules les journées validées sont payées
      supabase.from('affectations')
        .select('date, montant, superviseur_id')
        .eq('type', 'ASSISTANAT').eq('statut', 'valide').gte('date', debut).lte('date', fin)
    ])

    setSuperviseurs(sups || [])
    setSeances(sea || [])
    setFinancier(saisies || [])
    setAccueil(acc || [])
    setReunions(reu || [])
    setAssistanat(assist || [])
    setLoading(false)
  }

  const estPole = vue === 'SPA' || vue === 'HYDRO'
  const estGlobal = vue === 'GLOBAL'
  const types = estGlobal ? TYPES : [vue]

  // Mois de l'année scolaire : sept (9) → août (8)
  const moisScolaires = [9,10,11,12,1,2,3,4,5,6,7,8]
  const moisDe = d => parseInt(d.slice(5, 7), 10)

  // Séances du pôle (vues SPA / Hydro uniquement)
  const data = estPole ? seances.filter(s => s.pole === vue) : []

  // Encaissements agrégés par mois (depuis date_debut), vues SPA / Hydro
  const encaisseParMois = {}
  for (const m of moisScolaires) encaisseParMois[m] = 0
  for (const s of financier) {
    const m = new Date(s.date_debut).getMonth() + 1
    encaisseParMois[m] += (s.cb_pin || 0) + (s.cb_sans_contact || 0) + (s.cheque_comptant || 0) + (s.cheque_differe || 0) + (s.especes || 0)
  }

  // Lignes de rémunération, un type par ligne
  const lignes = []
  for (const s of seances) {
    if (!types.includes(s.pole)) continue
    for (const id of [s.superviseur_id, s.superviseur2_id].filter(Boolean)) {
      lignes.push({ sup: id, mois: moisDe(s.date), type: s.pole, montant: s.remuneration_affichee || 0, reelle: s.remuneration_reelle || 0, ca_ttc: s.ca_ttc || 0, jour: 1 })
    }
  }
  if (types.includes('ACCUEIL')) for (const a of accueil) lignes.push({ sup: a.superviseur_id, mois: moisDe(a.date), type: 'ACCUEIL', montant: a.montant || 0, jour: 1 })
  if (types.includes('ASSISTANAT')) for (const a of assistanat) lignes.push({ sup: a.superviseur_id, mois: moisDe(a.date), type: 'ASSISTANAT', montant: a.montant || 0, jour: 1 })
  if (types.includes('REUNIONS')) for (const r of reunions) lignes.push({ sup: r.superviseur_id, mois: moisDe(r.date), type: 'REUNIONS', montant: r.montant || 0, jour: 1 })

  // Grille superviseur x mois, plus totaux par mois, par type et général
  const grid = {}
  for (const sup of superviseurs) {
    grid[sup.id] = { superviseur: sup, mois: {}, types: {}, total: vide() }
    for (const m of moisScolaires) grid[sup.id].mois[m] = vide()
    for (const t of TYPES) grid[sup.id].types[t] = vide()
  }
  const totauxMois = {}
  for (const m of moisScolaires) {
    totauxMois[m] = { ...vide(), types: {} }
    for (const t of TYPES) totauxMois[m].types[t] = vide()
  }
  const totauxTypes = {}
  for (const t of TYPES) totauxTypes[t] = vide()
  const totalGeneral = vide()

  for (const l of lignes) {
    const g = grid[l.sup]
    if (!g) continue
    ajouter(g.mois[l.mois], l)
    ajouter(g.types[l.type], l)
    ajouter(g.total, l)
    ajouter(totauxMois[l.mois], l)
    ajouter(totauxMois[l.mois].types[l.type], l)
    ajouter(totauxTypes[l.type], l)
    ajouter(totalGeneral, l)
  }

  // KPI des vues SPA / Hydro
  const totauxAnnee = {
    ca_ttc: data.reduce((a, s) => a + (s.ca_ttc || 0), 0),
    rem_reelle: data.reduce((a, s) => a + (s.remuneration_reelle || 0), 0),
    rem_affichee: data.reduce((a, s) => a + (s.remuneration_affichee || 0), 0),
    seances: data.length,
    massages: data.reduce((a, s) => a + (s.total_massage_payant || 0) + (s.total_massage_gratuit || 0), 0),
    encaisse: financier.reduce((a, s) => a + (s.cb_pin || 0) + (s.cb_sans_contact || 0) + (s.cheque_comptant || 0) + (s.cheque_differe || 0) + (s.especes || 0), 0),
  }

  const rows = Object.values(grid).filter(r => r.total.jours > 0)
  const moisActifs = moisScolaires.filter(m => totauxMois[m].jours > 0 || (estPole && encaisseParMois[m] > 0))
  const unite = estGlobal ? 'Jours' : UNITE[vue]
  const labelVue = VUES.find(x => x.v === vue).label

  // Label du mois avec l'année correcte
  function labelMois(m) {
    const a = m >= 9 ? annee : annee + 1
    return `${MOIS_LABELS[m].slice(0,3)} ${String(a).slice(2)}`
  }

  const tiret = <span className="text-muted">—</span>

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Récapitulatif annuel</h2>
          <div className="page-header-sub">Année scolaire {labelAnneeScolaire(annee)}</div>
        </div>
      </div>

      <div style={{display:'flex', gap:12, marginBottom:24, flexWrap:'wrap'}}>
        <select value={annee} onChange={e => setAnnee(+e.target.value)}
          style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
          {listeAnneesScolaires().map(a => <option key={a} value={a}>{labelAnneeScolaire(a)}</option>)}
        </select>
        <select value={vue} onChange={e => setVue(e.target.value)}
          style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
          {VUES.map(x => <option key={x.v} value={x.v}>{x.label}</option>)}
        </select>
      </div>

      {estPole ? (
        <div className="stats-grid" style={{marginBottom:24}}>
          <div className="stat-card">
            <div className="s-label">CA TTC massages {labelAnneeScolaire(annee)}</div>
            <div className="s-value">{formatEuro(totauxAnnee.ca_ttc)}</div>
          </div>
          <div className="stat-card">
            <div className="s-label">CA encaissé TTC</div>
            <div className="s-value">{formatEuro(totauxAnnee.encaisse)}</div>
          </div>
          <div className="stat-card">
            <div className="s-label">Rém. réelle</div>
            <div className="s-value">{formatEuro(totauxAnnee.rem_reelle)}</div>
          </div>
          <div className="stat-card">
            <div className="s-label">Rém. à facturer</div>
            <div className="s-value text-bordeaux">{formatEuro(totauxAnnee.rem_affichee)}</div>
          </div>
          <div className="stat-card">
            <div className="s-label">Jours</div>
            <div className="s-value">{totauxAnnee.seances}</div>
            <div className="s-sub">{totauxAnnee.massages} massages</div>
          </div>
        </div>
      ) : (
        <div className="stats-grid" style={{marginBottom:24}}>
          <div className="stat-card">
            <div className="s-label">{estGlobal ? 'Total facturé' : `Total ${labelVue}`}</div>
            <div className="s-value text-bordeaux">{formatEuro(totalGeneral.montant)}</div>
          </div>
          {estGlobal
            ? TYPES.map(t => (
                <div className="stat-card" key={t}>
                  <div className="s-label">{LABEL_TYPE[t]}</div>
                  <div className="s-value">{formatEuro(totauxTypes[t].montant)}</div>
                  <div className="s-sub">{totauxTypes[t].jours} {UNITE[t].toLowerCase()}</div>
                </div>
              ))
            : <>
                <div className="stat-card">
                  <div className="s-label">{unite}</div>
                  <div className="s-value">{totalGeneral.jours}</div>
                </div>
                <div className="stat-card">
                  <div className="s-label">Superviseurs</div>
                  <div className="s-value">{rows.length}</div>
                </div>
              </>}
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <>
          <div className="card" style={{overflowX:'auto', marginBottom:20}}>
            <h3 style={{marginBottom:16}}>{estPole ? 'Rémunérations par superviseur' : `Rémunérations par superviseur · ${labelVue}`}</h3>
            <table>
              <thead>
                <tr>
                  <th>Superviseur</th>
                  {moisActifs.map(m => <th key={m} className="num" style={{fontSize:10}}>{labelMois(m)}</th>)}
                  {estGlobal && TYPES.map(t => <th key={t} className="num">{LABEL_TYPE[t]}</th>)}
                  <th className="num">{estGlobal ? 'Total facturé' : 'Total rém.'}</th>
                  {estPole && <th className="num">Rém. réelle</th>}
                  {!estGlobal && <th className="num">{unite}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.superviseur.id}>
                    <td style={{whiteSpace:'nowrap'}}><strong>{row.superviseur.prenom} {row.superviseur.nom}</strong></td>
                    {moisActifs.map(m => (
                      <td key={m} className="num">
                        {row.mois[m].jours > 0
                          ? <span style={{color:'var(--bordeaux)'}}>{formatEuro(row.mois[m].montant)}</span>
                          : tiret}
                      </td>
                    ))}
                    {estGlobal && TYPES.map(t => (
                      <td key={t} className="num">{row.types[t].jours > 0 ? formatEuro(row.types[t].montant) : tiret}</td>
                    ))}
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(row.total.montant)}</strong></td>
                    {estPole && <td className="num text-muted">{formatEuro(row.total.reelle)}</td>}
                    {!estGlobal && <td className="num">{row.total.jours}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  {moisActifs.map(m => (
                    <td key={m} className="num">
                      {totauxMois[m].jours > 0
                        ? <strong>{formatEuro(totauxMois[m].montant)}</strong>
                        : tiret}
                    </td>
                  ))}
                  {estGlobal && TYPES.map(t => (
                    <td key={t} className="num"><strong>{formatEuro(totauxTypes[t].montant)}</strong></td>
                  ))}
                  <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(estPole ? totauxAnnee.rem_affichee : totalGeneral.montant)}</strong></td>
                  {estPole && <td className="num text-muted"><strong>{formatEuro(totauxAnnee.rem_reelle)}</strong></td>}
                  {!estGlobal && <td className="num"><strong>{estPole ? totauxAnnee.seances : totalGeneral.jours}</strong></td>}
                </tr>
              </tfoot>
            </table>
            {(estGlobal || vue === 'ASSISTANAT' || vue === 'REUNIONS') && (
              <div className="text-muted" style={{fontSize:12, marginTop:12}}>
                {estGlobal && 'Global = SPA + Hydro + Accueil + Assistanat + Réunions. '}
                {(estGlobal || vue === 'ASSISTANAT') && 'Assistanat : journées validées uniquement. '}
                {(estGlobal || vue === 'REUNIONS') && 'Les réunions sont facturées sur la facture de supervision.'}
              </div>
            )}
          </div>

          <div className="card" style={{overflowX:'auto'}}>
            <h3 style={{marginBottom:16}}>{estPole ? 'CA et encaissements par mois' : 'Rémunérations par mois'}</h3>
            <table>
              <thead>
                <tr>
                  <th>Mois</th>
                  {estPole && <>
                    <th className="num">CA TTC massages</th>
                    <th className="num">CA encaissé TTC</th>
                    <th className="num">Jours</th>
                    <th className="num">Rém. facturée</th>
                  </>}
                  {estGlobal && <>
                    {TYPES.map(t => <th key={t} className="num">{LABEL_TYPE[t]}</th>)}
                    <th className="num">Total facturé</th>
                  </>}
                  {!estPole && !estGlobal && <>
                    <th className="num">{unite}</th>
                    <th className="num">Montant</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {moisActifs.map(m => (
                  <tr key={m}>
                    <td>{MOIS_LABELS[m]} {m >= 9 ? annee : annee + 1}</td>
                    {estPole && <>
                      <td className="num">{formatEuro(totauxMois[m].ca_ttc)}</td>
                      <td className="num">{encaisseParMois[m] > 0 ? formatEuro(encaisseParMois[m]) : tiret}</td>
                      <td className="num">{totauxMois[m].jours || tiret}</td>
                      <td className="num" style={{color:'var(--bordeaux)'}}>{formatEuro(totauxMois[m].montant)}</td>
                    </>}
                    {estGlobal && <>
                      {TYPES.map(t => (
                        <td key={t} className="num">{totauxMois[m].types[t].jours > 0 ? formatEuro(totauxMois[m].types[t].montant) : tiret}</td>
                      ))}
                      <td className="num" style={{color:'var(--bordeaux)'}}>{formatEuro(totauxMois[m].montant)}</td>
                    </>}
                    {!estPole && !estGlobal && <>
                      <td className="num">{totauxMois[m].jours || tiret}</td>
                      <td className="num" style={{color:'var(--bordeaux)'}}>{formatEuro(totauxMois[m].montant)}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total {labelAnneeScolaire(annee)}</strong></td>
                  {estPole && <>
                    <td className="num"><strong>{formatEuro(totauxAnnee.ca_ttc)}</strong></td>
                    <td className="num"><strong>{formatEuro(totauxAnnee.encaisse)}</strong></td>
                    <td className="num"><strong>{totauxAnnee.seances}</strong></td>
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totauxAnnee.rem_affichee)}</strong></td>
                  </>}
                  {estGlobal && <>
                    {TYPES.map(t => <td key={t} className="num"><strong>{formatEuro(totauxTypes[t].montant)}</strong></td>)}
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totalGeneral.montant)}</strong></td>
                  </>}
                  {!estPole && !estGlobal && <>
                    <td className="num"><strong>{totalGeneral.jours}</strong></td>
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totalGeneral.montant)}</strong></td>
                  </>}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
